import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "@services/ui-components";
import PersonalModule from "./PersonalModule/PersonalModule";
import { useGetModulesListForTariffQuery } from "@me-team/host/main/Modules/graphql/modules.hooks";
import ServiceModule from "./ServiceModule/ServiceModule";
import ClientsModule from "./ClientsModule/ClientsModule";
import LogbookModule from "./LogbookModule/LogbookModule";
import OnlineBookingModule from "./OnlineBookingModule/OnlineBookingModule";
import WarehousesModule from "./WarehousesModule/WarehousesModule";
import {
    DependentModulesInterface,
    DisableModuleDataInterface,
    PaidInfoInterface,
} from "../../../../interfaces/interfaces";
import { CompanyAccess, LanguageProxy, ModuleInputType, ModuleProxy } from "@me-team/host/main/graphql/types";
import { useUpdateModulesWithRefetch } from "../../../../hooks/useUpdateModuleRelation";
import { SimpleModal } from "@services/ui-components/src/SimpleModal/SimpleModal";
import { Col, Container, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import ModulesService from "../../../../services/ModulesService/ModulesService";
import { PreloaderComponent } from "@services/ui-components/src/PreloaderComponent/PreloaderComponent";
import { ModulesTagName, SubModulesTagName } from "../../../../utils/enums";
import AccessService from "@me-team/host/services/AccessService";
import { flattenModules } from "@me-team/host/src/utils/utils";
import ToastComponent from "../../../ui-components/ToastComponent/ToastComponent";
import MenuModule from "./MenuModule/MenuModule";
import { DeepPartial } from "@apollo/client/utilities";
import LanguageModalForMenu from "./LanguageModalForMenu/LanguageModalForMenu";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";

type ModuleListProps = {
    paidInfo: PaidInfoInterface;
};

const initialDisableModalData: DisableModuleDataInterface = {
    isModalOpen: false,
    moduleName: "",
    moduleId: null,
    dependentModules: [],
};

const ModulesList: React.FC<ModuleListProps> = ({ paidInfo }) => {
    const { currentCompanyId, branchId } = useParams();
    const { t: translate } = useTranslation();
    const canManageModules = useRoleAccess(CompanyAccess.TariffModulesManage);
    const { data, loading, refetch } = useGetModulesListForTariffQuery({
        fetchPolicy: "network-only",
        variables: { companyId: Number(currentCompanyId) },
    });
    const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);

    const modules: ModuleProxy[] = data?.user?.company?.modules;

    useEffect(() => {
        if (modules) {
            const flatModules = flattenModules(modules);
            const accessServiceInstance = AccessService.getInstance();
            accessServiceInstance.setModules(flatModules);
        }
    }, [modules]);

    const [disableModalData, setDisableModalData] = useState<DisableModuleDataInterface>(initialDisableModalData);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [updateModules, showToast, setShowToast, toastText, isDisableMainModules] =
        useUpdateModulesWithRefetch(refetch);

    const updateModulesWithAccessGuard = async (
        companyId: number,
        updates: ModuleInputType[],
        branchId: number,
        isNeededToDisable?: boolean
    ) => {
        if (!canManageModules) return;
        await updateModules(companyId, updates, branchId, isNeededToDisable);
    };

    const openDisableModal = (modalData: DisableModuleDataInterface) => {
        if (!canManageModules) return;
        setDisableModalData(modalData);
    };

    const onCloseLanguageModal = () => {
        setIsLanguageModalOpen(false);
    };

    const onCloseDisabledModalHandler = () => {
        setDisableModalData(initialDisableModalData);
    };
    const onSubmitDisabledModal = async () => {
        if (!canManageModules) return;

        setIsLoading(true);
        const isLogbook: boolean = disableModalData.moduleName === ModulesTagName.LOGBOOK;

        let filteredDependentModules: DependentModulesInterface[] = disableModalData.dependentModules.filter(
            (dependentModule) => {
                return (
                    dependentModule.name !== SubModulesTagName.REVIEWS &&
                    dependentModule.name !== SubModulesTagName.STATISTIC
                );
            }
        );

        if (isLogbook) {
            filteredDependentModules = filteredDependentModules.filter(
                (dependentModule) => dependentModule.name !== ModulesTagName.LOGBOOK
            );
        }

        const updates: ModuleInputType[] = [
            { id: disableModalData.moduleId, isActive: false },
            ...filteredDependentModules.map(
                (dependentModule: DependentModulesInterface) => dependentModule.inputForRequest
            ),
        ];
        await updateModulesWithAccessGuard(Number(currentCompanyId), updates, Number(branchId), true);
        onCloseDisabledModalHandler();
        setIsLoading(false);
    };

    if (loading) return <Spinner />;
    if (!data) return null;

    const modulesList: ModuleProxy[] = data.user.company.modules;
    const primaryLanguages: DeepPartial<LanguageProxy> = data.user.company.primaryLanguage;
    const languages: DeepPartial<LanguageProxy[]> = data.user.company.languages;

    return (
        <>
            <PersonalModule
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                openDisableModal={openDisableModal}
                canManageModules={canManageModules}
            />
            <ServiceModule
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                openDisableModal={openDisableModal}
                canManageModules={canManageModules}
            />
            <ClientsModule
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                openDisableModal={openDisableModal}
                canManageModules={canManageModules}
            />
            <LogbookModule
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                openDisableModal={openDisableModal}
                setIsLoading={setIsLoading}
                isDisableMainModules={isDisableMainModules || !canManageModules}
                canManageModules={canManageModules}
            />
            <OnlineBookingModule
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                setIsLoading={setIsLoading}
                openDisableModal={openDisableModal}
                isDisableMainModules={isDisableMainModules || !canManageModules}
                canManageModules={canManageModules}
            />
            {/*{!!paidInfo.planPrice?.length && (*/}
            {/*    <WarehousesModule*/}
            {/*        modulesList={modulesList}*/}
            {/*        paidInfo={paidInfo}*/}
            {/*        updateModules={updateModules}*/}
            {/*    />*/}
            {/*)}*/}

            <WarehousesModule
                modulesList={modulesList}
                paidInfo={paidInfo}
                updateModules={updateModulesWithAccessGuard}
                canManageModules={canManageModules}
            />
            <MenuModule
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                isDisableMainModules={isDisableMainModules || !canManageModules}
                isLanguagesSubmitted={Boolean(primaryLanguages) && languages.length > 0}
                setIsLanguageModalOpen={setIsLanguageModalOpen}
                canManageModules={canManageModules}
            />
            <LanguageModalForMenu
                isOpen={isLanguageModalOpen}
                onClose={onCloseLanguageModal}
                modulesList={modulesList}
                updateModules={updateModulesWithAccessGuard}
                canManageModules={canManageModules}
            />
            <SimpleModal
                isOpen={disableModalData.isModalOpen}
                onClose={onCloseDisabledModalHandler}
                title={translate("Disabling dependent modules")}
                description={ModulesService.getDescForDependModal(disableModalData, translate)}
            >
                <Container>
                    <Row>
                        <Col xs={6} className={"ps-0"}>
                            <Button
                                variant="outline-primary"
                                className={"w-100 fw-normal"}
                                onClick={onCloseDisabledModalHandler}
                            >
                                {translate("Cancel")}
                            </Button>
                        </Col>
                        <Col xs={6} className={"pe-0"}>
                            <Button
                                variant="primary"
                                className={"w-100 fw-normal"}
                                onClick={onSubmitDisabledModal}
                                disabled={!canManageModules}
                            >
                                {translate("Disable all")}
                            </Button>
                        </Col>
                    </Row>
                </Container>
            </SimpleModal>
            {isLoading && <PreloaderComponent />}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </>
    );
};

export default ModulesList;
